import { isValidElement, ReactElement, ReactNode } from 'react';
import * as Sentry from '@sentry/react';
import type { FallbackRender } from '@sentry/react';

interface Props {
  children: ReactNode;
  /**
   * Fallback UI to display when an error occurs.
   * Can be either:
   * - A React element (e.g., `<p>Error</p>`)
   * - A render function that receives error details and resetError callback
   */
  fallback?: ReactElement | FallbackRender;
}

const DefaultFallback: FallbackRender = ({ error, resetError }) => {
  const errorMessage = error instanceof Error ? error.message : String(error);

  return (
    <div style={{ padding: 20, textAlign: 'center' }}>
      <h1>Something went wrong</h1>
      <p>Please try refreshing the page.</p>
      {import.meta.env.DEV && (
        <pre style={{ textAlign: 'left', overflow: 'auto' }}>{errorMessage}</pre>
      )}
      <button
        onClick={resetError}
        style={{
          marginTop: 16,
          padding: '8px 16px',
          cursor: 'pointer',
        }}
      >
        Try again
      </button>
    </div>
  );
};

/**
 * Error boundary that catches React errors and reports them to Sentry.
 *
 * Uses @sentry/react's ErrorBoundary which:
 * - Automatically captures exceptions and sends them to Sentry
 * - Provides error reset functionality
 *
 * @example
 * // Simple fallback element
 * <ErrorBoundary fallback={<p>Something went wrong</p>}>
 *   <MyComponent />
 * </ErrorBoundary>
 *
 * @example
 * // Fallback with error details and reset button
 * <ErrorBoundary fallback={({ error, resetError }) => (
 *   <div>
 *     <p>Error: {error.message}</p>
 *     <button onClick={resetError}>Retry</button>
 *   </div>
 * )}>
 *   <MyComponent />
 * </ErrorBoundary>
 *
 * @see https://docs.sentry.io/platforms/javascript/guides/react/features/error-boundary/
 */
export function ErrorBoundary({ children, fallback }: Props) {
  // Convert ReactElement to FallbackRender if needed
  const fallbackRender: FallbackRender =
    fallback === undefined
      ? DefaultFallback
      : isValidElement(fallback)
        ? () => fallback
        : fallback;

  return (
    <Sentry.ErrorBoundary
      fallback={fallbackRender}
      onError={(error, componentStack) => {
        // Log locally for development debugging
        console.error('[ErrorBoundary] Uncaught error:', error);
        if (componentStack) {
          console.error('[ErrorBoundary] Component stack:', componentStack);
        }
      }}
    >
      {children}
    </Sentry.ErrorBoundary>
  );
}
